/**
 * @desc 日历组件默认配置文案相关信息
 * @link http://design.umu.work/#/umu_design/src/components/date-picker
 */
import { lang, getCurrentLang } from '@umu/ukit-lang';
import dayjs from 'dayjs';

import { ESplitType, IResRangeByDay } from '../interface';
import head from 'lodash/head';
import last from 'lodash/last';
type Dayjs = dayjs.Dayjs;
const currentLang = getCurrentLang();
/**
 * @desc 默认分隔符
 */
const getPickerSeparator = (currentLang?: string) => {
    const localKey = (currentLang || getCurrentLang() || 'en').toLocaleLowerCase();
    switch (localKey) {
        case 'cn':
        case 'tw':
            return lang('至');
        default:
            return '-';
    }
};
/**
 * @desc locale 默认配置
 */
const getDatePickerLocale = (_currentLang?: string) => {
    return {
        backToToday: lang('返回今天'),
        clear: lang('清除'),
        dateFormat: lang('YYYY年M月D日'),
        dateSelect: lang('选择日期'),
        dateTimeFormat: lang('YYYY年M月D日 HH时mm分ss秒'),
        dayFormat: lang('D日'),
        decadeSelect: lang('选择年代'),
        locale: 'zh_CN',
        month: lang('月'),
        monthSelect: lang('选择月份'),
        nextCentury: lang('下一世纪'),
        nextDecade: lang('下一年代'),
        nextMonth: lang('下个月 (翻页下键)'),
        nextYear: lang('下一年 (Control键加右方向键)'),
        now: lang('此刻'),
        ok: lang('确定'),
        previousCentury: lang('上一世纪'),
        previousDecade: lang('上一年代'),
        previousMonth: lang('上个月 (翻页上键)'),
        previousYear: lang('上一年 (Control键加左方向键)'),
        timeSelect: lang('选择时间'),
        today: lang('今天'),
        weekSelect: lang('选择周'),
        year: lang('年'),
        yearFormat: lang('YYYY年'),
        yearSelect: lang('选择年份'),
    };
};
/**
 * @desc date范围选择器默认配置
 */
const defaultRangeConfig = {
    currentLang,
    placeholder: [lang('开始日期'), lang('结束日期')],
    separator: getPickerSeparator(currentLang),
    footerInfo: {
        permissions: {
            cancel: {
                label: lang('取消'),
            },
            confirm: {
                label: lang('确认'),
            },
            unlimited: {
                isShow: true,
                label: lang('不限'),
            },
        },
    },
};

/**
 * @desc date选择器默认配置
 */
const defaultPickerConfig = {
    currentLang,
    footerInfo: {
        permissions: {
            cancel: {
                label: lang('取消'),
                isHidden: true,
            },
            confirm: {
                label: lang('确认'),
            },
            unlimited: {
                label: lang('不限'),
            },
        },
    },
};
/**
 * @desc 判断是否在今天之前或之后
 * @param current 当前日期
 * @param isAfter 是否在今天之后
 * @param disabledDate 禁用日期
 */
const onDisabledDateToday = (
    current: Dayjs,
    info?: {
        isAfter?: boolean;
        isInCludeToDate?: boolean;
    },
    disabledDate = dayjs().startOf('day'),
) => {
    const currentDate = current.startOf('day');
    const { isAfter = false, isInCludeToDate = false } = info || {};
    if (isAfter) {
        return currentDate.isAfter(disabledDate) || (isInCludeToDate && currentDate.isSame(disabledDate));
    }
    return currentDate.isBefore(disabledDate) || (isInCludeToDate && currentDate.isSame(disabledDate));
};
/**
 * @desc 判断两个日期是否相等
 * @param currentDate 当前日期
 * @param compareDate 比较日期
 * @param format 格式化
 */
const onEqualDate = (current: Dayjs | string, compareDate = dayjs().startOf('day'), format?: 'YYYY-MM-DD') => {
    if (typeof current === 'string') {
        return dayjs(current).isSame(compareDate.format(format));
    }
    const currentDate = current.startOf('day');
    return currentDate.isSame(compareDate.format(format));
};
/**
 * @desc 获取指定区间日期范围
 * @param count （天数｜月｜年）个数
 * @param info 配置信息 {info.isAfter 是否在今天之后, info.date 当前日期, info.splitType 间隔类型, info.isInCludeToDate 是否包含当前日期, info.format 格式化, info.disabledDate 禁用日期回调函数}
 * @returns {range: [其实日期, 截止日期], list: [符合要求的日期数组]}
 */
const getSpecifiedIntervalDateRange = (
    count: number,
    info?: {
        isAfter?: boolean; // 是否在今天之后
        splitType?: ESplitType; // 间隔类型
        date?: Dayjs; // 当前日期
        format?: string; // 格式化
        isInCludeToDate?: boolean; // 是否包含当前日期
        disabledDate?: (date: Dayjs) => boolean; // 禁用日期回调函数
    },
): IResRangeByDay => {
    const {
        isAfter = false,
        date = dayjs(),
        format = 'YYYY-MM-DD',
        isInCludeToDate = false,
        splitType = ESplitType.day,
    } = info || {};
    const disabledDate = info?.disabledDate || (() => false);
    const [startDate, endDate] = isAfter
        ? [date, date.add(count, splitType as any)]
        : [date.subtract(count, splitType as any), date];
    let loopDate = startDate;
    const list: string[] = [];
    while (loopDate.isBefore(endDate)) {
        const isDisabled = disabledDate(startDate);
        if (!isDisabled) {
            list.push(loopDate.format(format));
        }
        loopDate = loopDate.add(1, splitType as any);
    }
    if (isInCludeToDate) {
        if (isAfter) {
            list.unshift(date.format(format));
            list.splice(list.length - 1, 1);
        } else {
            list.push(date.format(format));
            list.splice(0, 1);
        }
    }
    const firstDate = head(list) || '';
    const lastDate = last(list) || '';
    return {
        range: [firstDate, lastDate],
        rangeDayjs: [onTransformDayjs(firstDate), onTransformDayjs(lastDate)],
        list,
    };
};
const onTransformDayjs = (date: string) => {
    return dayjs(date);
};
export const datePickerConfig = {
    defaultRangeConfig,
    onEqualDate,
    onTransformDayjs,
    getDatePickerLocale,
    getSpecifiedIntervalDateRange,
    onDisabledDateToday,
    defaultPickerConfig,
    getPickerSeparator,
};
